import { create } from "zustand";

export type ToastVariant = "default" | "success" | "error" | "warning";

const MAX_TOASTS = 4;
export const DEFAULT_TTL = 4000;
export const ERROR_TTL = 8000;

export interface ToastItem {
  id: string;
  title: string;
  description?: string;
  variant?: ToastVariant;
  count: number;
  createdAt: number;
  key: string;
}

interface ToastState {
  items: ToastItem[];
  addToast: (toast: Omit<ToastItem, "id" | "count" | "createdAt" | "key">) => void;
  dismissToast: (id: string) => void;
}

const buildKey = (toast: {
  title: string;
  description?: string;
  variant?: ToastVariant;
}) => `${toast.title}::${toast.description ?? ""}::${toast.variant ?? "default"}`;

const BATCH_WINDOW = 800;

export const toastStore = create<ToastState>((set) => {
  const pending: Record<string, { count: number; timerId: number }> = {};

  const flushPending = (key: string) => {
    const entry = pending[key];
    if (!entry) {
      return;
    }
    delete pending[key];

    set((state) => {
      const [title, description, variant] = key.split("::");
      const newItem: ToastItem = {
        id: crypto.randomUUID(),
        title,
        description: description || undefined,
        variant: (variant as ToastVariant) ?? "default",
        count: entry.count,
        createdAt: Date.now(),
        key,
      };

      const items = [newItem, ...state.items];
      if (items.length > MAX_TOASTS) {
        items.pop();
      }

      return { items };
    });
  };

  return {
    items: [],
    addToast: (toast) => {
      const key = buildKey(toast);

      if (pending[key]) {
        pending[key].count += 1;
        clearTimeout(pending[key].timerId);
        pending[key].timerId = window.setTimeout(
          () => flushPending(key),
          BATCH_WINDOW,
        );
        return;
      }

      const timerId = window.setTimeout(() => flushPending(key), BATCH_WINDOW);
      pending[key] = { count: 1, timerId };
    },
    dismissToast: (id) =>
      set((state) => ({ items: state.items.filter((item) => item.id !== id) })),
  };
});

export const showToast = (
  toast: Omit<ToastItem, "id" | "count" | "createdAt" | "key">,
) => {
  toastStore.getState().addToast(toast);
};
